import type {
  AiCampaignForm,
  AiPreviewData,
} from '@/features/campaigns/types/campaign';

const STORAGE_KEY = 'screenista.ai.campaign.draft';

export type AiCampaignDraft = {
  formData: AiCampaignForm;
  aiGeneratedData: AiPreviewData | null;
  campaignName?: string;
  couponCode?: string;
  savedAt: number;
};

function canUseStorage() {
  return typeof window !== 'undefined' && !!window.sessionStorage;
}

export function loadAiCampaignDraft(): AiCampaignDraft | null {
  if (!canUseStorage()) return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AiCampaignDraft;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveAiCampaignDraft(
  draft: Omit<AiCampaignDraft, 'savedAt'> & { savedAt?: number },
) {
  if (!canUseStorage()) return;
  try {
    const payload: AiCampaignDraft = {
      ...draft,
      savedAt: draft.savedAt ?? Date.now(),
    };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota / private mode — ignore
  }
}

export function patchAiCampaignDraft(
  patch: Partial<Omit<AiCampaignDraft, 'savedAt'>>,
) {
  const current = loadAiCampaignDraft();
  const formData = patch.formData ?? current?.formData;
  if (!formData) return;
  saveAiCampaignDraft({
    formData,
    aiGeneratedData:
      patch.aiGeneratedData !== undefined
        ? patch.aiGeneratedData
        : (current?.aiGeneratedData ?? null),
    campaignName: patch.campaignName ?? current?.campaignName,
    couponCode: patch.couponCode ?? current?.couponCode,
  });
}

export function clearAiCampaignDraft() {
  if (!canUseStorage()) return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
